import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
// Copyright   : 2020 semantics GmbH
// Date        : $Date: 2026-05-25 21:10:12 +0200 (Mon, 25 May 2026) $
// Author      : Dirk Rothe
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 163262 $
// Purpose     : choose table columns
// =============================================================================
// ---Third-party imports
import React from 'react';
import { Icon, Menu, List, Image, Grid, Header, Button } from 'semantic-ui-react';
import clsx from 'clsx';
// ---Framework imports
import log from 'uix/logging';
import { Sel } from 'uix/registries';
import { XTab, HeaderSegment, XMenu, ResizableDialog, DebugFeature } from 'uix/components';
import { keys, mapItems, groupBy, sorted, uniq, dict, size, strip } from 'ldash';
import { useSelector, useActions, useState, useGraphics, useEffect } from 'uix/hooks';
import { checkAcl } from 'uix/auth/acl';
// ---Local imports
import { useTreeFieldSchema } from './hooks';
import { ColumnsById } from './columns';
import { getColumLabel, getColumnNamespaceLabel, useColumnContext } from './columnTypes';
import styles from './styles.css';
import { VIcon } from 'uix/graphics';
// ---Globals
const GENERAL = '_' // alpha sorted first
;
// read function names
const cleanColumns = (coltype)=>coltype.endsWith('Column') ? coltype.slice(0, -6) : coltype;
const coltype = (f)=>{
    let n = ColumnsById[f]?.name;
    return cleanColumns(n || '');
};
// XXX: ColumnsById is inplace mutated by column registration
let ColumnsNs = groupBy(keys(ColumnsById), (f)=>f.includes('.') ? f.split('.')[0] : GENERAL);
//~ log.x(keys(ColumnsById).map(k => [k, coltype(k), ColumnsById[k]?.coltype]))
const sortedCols = (unordered, ordered)=>sorted(unordered, (colid)=>ordered.indexOf(colid));
// ---Exports
export const ColumnLister = ({ selected, unchosableColumns, chosableColumns, onChange, datatypes, quickUnselect = false })=>{
    let unchosableColumnsSet = new Set(unchosableColumns);
    let chosableColumnsSet = new Set(chosableColumns);
    let user = useSelector(Sel.user);
    let [colFilter, setColFilter] = useState('');
    let filterByColFilter = (colids)=>{
        //~ let colFilter = 'metadaten'
        //~ return colids.slice(10, 15)
        colFilter = strip(colFilter).toLowerCase();
        if (colFilter.length > 2) {
            colids = colids.filter((f)=>{
                let s = translate_field(f, false);
                return s.toLowerCase().includes(colFilter) || f.includes(colFilter);
            });
        }
        return colids;
    };
    let filterBychosable = (colids)=>{
        let res = colids.filter((f)=>!chosableColumns?.length || chosableColumnsSet.has(f) || !f.includes('.'));
        res = res.filter((colid)=>{
            let acl = ColumnsById[colid]?.options?.acl;
            return !acl || checkAcl(acl, user);
        });
        return res;
    };
    let { columnLabel, columnNamespaceLabel, hiddenColumns } = useColumnContext();
    let schema = useTreeFieldSchema();
    let itypes = useSelector(Sel.itypes);
    let i18n = useSelector(Sel.i18n);
    let [debugOn, setDebugOn] = useState(false);
    let graphics = useGraphics([
        'types'
    ]);
    let [active, setActive] = useState(0);
    let [hover, setHover] = useState(false);
    let [selection, setSelection] = useState({});
    // reset on external selected change
    useEffect(()=>{
        setSelection(dict((selected || []).map((e)=>[
                e,
                true
            ])));
    }, [
        selected
    ]);
    let _setSelection = (sel)=>{
        setSelection(sel);
        if (onChange) {
            let colids = mapItems(sel, (colid, selected)=>colid, (colid, selected)=>selected);
            if (size(chosableColumns) > 0) {
                colids = sortedCols(colids, chosableColumns);
            }
            onChange(colids);
        }
    };
    let toggleSelection = (f)=>_setSelection({
            ...selection,
            [f]: !selection[f]
        });
    let clearSelection = ()=>_setSelection({});
    const mouseEnter = (i)=>{
        setHover(i);
    };
    const mouseLeave = (i)=>{
        setHover(false);
    };
    let translate_field = (f, span)=>{
        if (debugOn) return f;
        let k = ColumnsById[f]?.options?.i18nKey || `col_${f}`;
        return getColumLabel(f, i18n, k, columnLabel, span);
    };
    let translate_ns = (ns, span)=>debugOn ? ns : getColumnNamespaceLabel(ns, i18n, columnNamespaceLabel, span);
    let filter_fields = (ns, colids)=>{
        if (ColumnsNs[ns]) {
            // merge non basic schema fields from colums spec
            colids = uniq([
                ...colids,
                ...filterBychosable(ColumnsNs[ns])
            ]);
        }
        colids = colids.filter((f)=>!hiddenColumns.has(f));
        if (!debugOn) {
            colids = colids.filter((f)=>ColumnsById[f]);
        }
        return colids;
    };
    let build = (datatype_fields)=>{
        // there are some synthetic cols (options={fields: ....}) that need to be collected
        // by checking if their options.fields intersect with datatype_fields
        let df = new Set(datatype_fields);
        let synth_colids = mapItems(ColumnsById, (colid)=>colid, (colid, { options })=>df.has(options?.fields?.[0]));
        // combine all candidates
        let colids = [
            ...synth_colids,
            ...datatype_fields
        ];
        colids = filterBychosable(colids);
        //XXX: instead of metaparent.* - this includes metaparent.field1, metaparent.field2
        colids = filterByColFilter(colids);
        //~ log.x(colids)
        let by_ns = groupBy(colids, (f)=>f.includes('.') ? f.split('.')[0] : GENERAL);
        let alpha_sort = sorted(keys(by_ns));
        let ns2fields = alpha_sort.map((ns)=>[
                ns,
                filter_fields(ns, by_ns[ns])
            ]);
        const [ICON_CHECKED, ICON_UN_CHECKED, ICON_LOCKED, ICON_INDETERMINATE] = [
            'check square outline',
            'square outline',
            'lock',
            'minus square outline'
        ];
        let get_state_icon = (ns_fields, ignore_unchosable = false)=>{
            let _unchosableColumnsSet = ignore_unchosable ? new Set() : unchosableColumnsSet;
            if (ns_fields.every((f)=>_unchosableColumnsSet.has(f) || selection[f])) {
                return ICON_CHECKED;
            } else if (ns_fields.every((f)=>!(_unchosableColumnsSet.has(f) || selection[f]))) {
                return ICON_UN_CHECKED;
            }
            return ICON_INDETERMINATE;
        };
        let toggleGroupClick = (e, ns, ns_fields)=>{
            e.stopPropagation();
            let icon_state = get_state_icon(ns_fields, true);
            let next_selected_state = ![
                ICON_CHECKED,
                ICON_INDETERMINATE
            ].includes(icon_state);
            let new_selection = dict(ns_fields.filter((f)=>!unchosableColumnsSet.has(f)).map((f)=>[
                    f,
                    next_selected_state
                ]));
            _setSelection({
                ...selection,
                ...new_selection
            });
        };
        let ns2fields_sorted = sorted(ns2fields, {
            key: ([ns, fields])=>translate_ns(ns == GENERAL ? 'general' : ns, false),
            localcmp: true
        });
        let ns_groups = ns2fields_sorted.filter(([ns, ns_fields])=>ns_fields.length).map(([ns, ns_fields], i)=>{
            return /*#__PURE__*/ _jsxs(XMenu.Item, {
                onMouseEnter: ()=>mouseEnter(i),
                onMouseLeave: ()=>mouseLeave(i),
                onClick: ()=>setActive(i),
                active: active === i,
                index: i,
                className: styles.fieldGroupItem,
                title: translate_ns(ns == GENERAL ? 'general' : ns, false),
                children: [
                    /*#__PURE__*/ _jsx(Icon, {
                        size: "large",
                        name: get_state_icon(ns_fields),
                        onClick: (e)=>{
                            setActive(i);
                            toggleGroupClick(e, ns, ns_fields);
                        }
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: styles.title,
                        children: translate_ns(ns == GENERAL ? 'general' : ns, false)
                    }),
                    /*#__PURE__*/ _jsx(Icon, {
                        size: "large",
                        className: clsx(hover === i || active === i ? null : styles.hidden),
                        name: 'angle double right',
                        onClick: ()=>setActive(i)
                    })
                ]
            }, ns);
        });
        let ns_content = ns2fields_sorted.filter(([ns, ns_fields])=>ns_fields.length).map(([ns, ns_fields], i)=>{
            //~ if (ns == 'metaparent') log.x(ns_fields)
            ns_fields = filterByColFilter(ns_fields);
            //~ if (ns == 'metaparent') log.x(ns_fields)
            return ns_fields.map((f)=>{
                if (active === i) {
                    let is_locked = unchosableColumnsSet.has(f);
                    return /*#__PURE__*/ _jsxs(XMenu.Item, {
                        active: selection[f],
                        disabled: is_locked,
                        onClick: ()=>!is_locked && toggleSelection(f),
                        className: clsx(is_locked && styles.lockedSelected),
                        children: [
                            /*#__PURE__*/ _jsx(VIcon, {
                                name: is_locked ? ICON_LOCKED : selection[f] ? ICON_CHECKED : ICON_UN_CHECKED,
                                size: "large"
                            }),
                            /*#__PURE__*/ _jsx(List.Header, {
                                children: translate_field(f)
                            }),
                            /*#__PURE__*/ _jsx(List.Description, {
                                children: coltype(f)
                            })
                        ]
                    }, f);
                }
            });
        });
        let onArrowClick = (e, direction, i)=>{
            e.stopPropagation();
            let s = selected[i];
            let new_selected = [
                ...selected
            ];
            if (direction == 'up') {
                new_selected[i] = new_selected[i - 1];
                new_selected[i - 1] = s;
            } else {
                new_selected[i] = new_selected[i + 1];
                new_selected[i + 1] = s;
            }
            _setSelection(dict(new_selected.map((s)=>[
                    s,
                    selection[s]
                ])));
        };
        let selList = selected.map((sel, i)=>{
            let header = translate_field(sel);
            if (sel.includes('.')) {
                let ns = sel.split('.')[0];
                header = /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        `${translate_ns(ns, false)}: `,
                        " ",
                        header
                    ]
                });
            }
            return /*#__PURE__*/ _jsxs(XMenu.Item, {
                className: styles.chosenClmn,
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: styles.sortButton,
                        children: [
                            i > 0 && /*#__PURE__*/ _jsx(Button, {
                                size: "tiny",
                                onClick: (e)=>onArrowClick(e, 'up', i),
                                icon: true,
                                secondary: true,
                                children: /*#__PURE__*/ _jsx(VIcon, {
                                    name: "chevron up"
                                })
                            }),
                            i < selected.length - 1 && /*#__PURE__*/ _jsx(Button, {
                                size: "tiny",
                                onClick: (e)=>onArrowClick(e, 'down', i),
                                icon: true,
                                secondary: true,
                                children: /*#__PURE__*/ _jsx(VIcon, {
                                    name: "chevron down"
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: styles.name,
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                style: {
                                    fontWeight: 'bold',
                                    marginBottom: '0'
                                },
                                children: header
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                children: coltype(sel)
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        size: "small",
                        onClick: ()=>toggleSelection(sel),
                        icon: true,
                        secondary: true,
                        children: /*#__PURE__*/ _jsx(VIcon, {
                            name: "close"
                        })
                    })
                ]
            }, i);
        });
        return /*#__PURE__*/ _jsxs(Grid, {
            className: styles.grid,
            style: {
                height: 'calc(100% - 60px)'
            },
            columns: 3,
            children: [
                /*#__PURE__*/ _jsxs(Grid.Row, {
                    children: [
                        /*#__PURE__*/ _jsx(Grid.Column, {
                            children: /*#__PURE__*/ _jsx(Header, {
                                as: "h2",
                                content: "Spaltenkategorien"
                            })
                        }),
                        /*#__PURE__*/ _jsx(Grid.Column, {
                            children: /*#__PURE__*/ _jsx(Header, {
                                as: "h2",
                                content: "Spalten auswählen"
                            })
                        }),
                        /*#__PURE__*/ _jsx(Grid.Column, {
                            children: /*#__PURE__*/ _jsx(Header, {
                                as: "h2",
                                content: "Ausgewählte Spalten"
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Grid.Row, {
                    divided: true,
                    children: [
                        /*#__PURE__*/ _jsx(Grid.Column, {
                            className: styles.overflow,
                            children: /*#__PURE__*/ _jsx(XMenu, {
                                vertical: true,
                                children: ns_groups
                            })
                        }),
                        /*#__PURE__*/ _jsx(Grid.Column, {
                            className: styles.overflow,
                            children: /*#__PURE__*/ _jsx(XMenu, {
                                vertical: true,
                                children: ns_content
                            })
                        }),
                        /*#__PURE__*/ _jsx(Grid.Column, {
                            className: styles.overflow,
                            children: /*#__PURE__*/ _jsx(XMenu, {
                                borderless: true,
                                vertical: true,
                                children: selList
                            })
                        })
                    ]
                })
            ]
        });
    };
    let allowed_datatype = new Set((datatypes || []).map((dt)=>String(dt)));
    let panes = mapItems(schema, (datatype, fields)=>({
            menuItem: /*#__PURE__*/ _jsxs(Menu.Item, {
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        style: {
                            marginLeft: 5
                        },
                        children: datatype != 10 ? /*#__PURE__*/ _jsx(Image, {
                            inline: true,
                            width: "14px",
                            src: graphics.types?.[`${datatype}_0`]
                        }) : /*#__PURE__*/ _jsx(Icon, {
                            name: "warehouse"
                        })
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        style: {
                            paddingLeft: 5,
                            paddingRight: 5
                        },
                        children: i18n.get(`types_datatype_${itypes.datatypeMap[datatype]}`)
                    })
                ]
            }, datatype),
            tabKey: datatype,
            render: ()=>build(fields)
        }), (dt)=>dt != 7 && allowed_datatype.has(dt));
    let selected_menu = quickUnselect ? mapItems(selection, (f)=>/*#__PURE__*/ _jsxs(Menu.Item, {
            fitted: true,
            onClick: ()=>toggleSelection(f),
            children: [
                /*#__PURE__*/ _jsx("span", {
                    style: {
                        marginLeft: 3
                    },
                    children: translate_field(f)
                }),
                /*#__PURE__*/ _jsx(Icon, {
                    style: {
                        marginTop: 3
                    },
                    name: "close"
                })
            ]
        }, f), (f, is_selected)=>is_selected) : null;
    let menu = /*#__PURE__*/ _jsxs(XMenu, {
        children: [
            /*#__PURE__*/ _jsx(XMenu.Input, {
                icon: "filter",
                value: colFilter,
                onValueChange: setColFilter,
                debounce: 1000
            }),
            selected_menu,
            /*#__PURE__*/ _jsx(DebugFeature, {
                children: /*#__PURE__*/ _jsx(XMenu.Item, {
                    active: debugOn,
                    icon: "bug",
                    onClick: ()=>setDebugOn(!debugOn)
                })
            }),
            selected.length > 0 && /*#__PURE__*/ _jsx(XMenu.Item, {
                icon: "eraser",
                title: "Clear column selection.",
                onClick: clearSelection
            })
        ]
    });
    return /*#__PURE__*/ _jsx(HeaderSegment, {
        embedded: true,
        header: i18n.get('col_chooser'),
        // subheader={panes.length == 1 ? panes[0].menuItem : null}
        menu: menu,
        className: styles.columnLister,
        icon: "tbl_columns",
        children: panes.length == 1 ? panes[0].render() : /*#__PURE__*/ _jsx(XTab, {
            menu: {
                tabular: true
            },
            storeKey: "ColumnLister",
            style: {
                height: 'calc(100% - 58px)'
            },
            className: styles.tabMenu,
            panes: panes
        })
    });
};
export const ColumnListerDialog = ({ onClose, ...props })=>/*#__PURE__*/ _jsx(ResizableDialog, {
        closeOnEsc: true,
        overflow: "hidden",
        onClose: onClose,
        children: /*#__PURE__*/ _jsx(ColumnLister, {
            ...props
        })
    });
